import { CommsCheckResult, XrdlgData, XrinvData, XritmData } from '../../types/comms-check';
import { makeReadableTimeStamp, parseSmetsVersion } from '../util';

export const proxy500L1error: CommsCheckResult = {
  xrinv: { data: [], address: null, state: 'PROXY_L1_ERROR' },
  xritm: { data: [], state: 'PROXY_L1_ERROR', msn: '' },
  xrdlg: { data: [], state: 'PROXY_L1_ERROR' },
};

export const proxy500L2error: CommsCheckResult = {
  xrinv: { data: [], address: null, state: 'PROXY_L2_ERROR' },
  xritm: { data: [], state: 'PROXY_L2_ERROR', msn: '' },
  xrdlg: { data: [], state: 'PROXY_L2_ERROR' },
};

export const unexpectedL1Error: CommsCheckResult = {
  xrinv: { data: [], address: null, state: 'UNEXPECTED_L1_ERROR' },
  xritm: { data: [], state: 'UNEXPECTED_L1_ERROR', msn: '' },
  xrdlg: { data: [], state: 'UNEXPECTED_L1_ERROR' },
};

export const unexpectedL2Error: CommsCheckResult = {
  xrinv: { data: [], address: null, state: 'UNEXPECTED_L2_ERROR' },
  xritm: { data: [], state: 'UNEXPECTED_L2_ERROR', msn: '' },
  xrdlg: { data: [], state: 'UNEXPECTED_L2_ERROR' },
};

export const isLCTActive = (lastCommunicatedTime: string | null): boolean | null => {
  if (lastCommunicatedTime === null) {
    return null;
  }
  // 6 hours in ms is 21600000
  const activeTimeInMs = 21600000;
  const lastCommunicatedTimeInMs = new Date(lastCommunicatedTime).getTime();
  const currentTimeInMs = new Date().getTime();
  const timeDifference = currentTimeInMs - lastCommunicatedTimeInMs;
  if (timeDifference < activeTimeInMs) {
    return true;
  }
  return false;
};

export function getXrinvData(l2Response: any): XrinvData[] {
  return l2Response.atomL2response['0']?.xml_string?.Body?.XRINV_OUT_02?.InventoryDevice?.map((device: any) => ({
    deviceId: device.DeviceID,
    deviceType: device.DeviceType,
    deviceStatus: device.DeviceStatus,
    mpxn: device.ImportMPXN,
    smetsDeviceType: parseSmetsVersion(device.DeviceGBCSVersion),
  }));
}

export function getXritmData(l2Response: any): XritmData[] {
  const readingRegister = l2Response.atomL2response['0']?.xml_string?.Body?.XRITM_OUT_02?.Readings?.TOURegister;
  if (!readingRegister) {
    return [];
  }

  if (Array.isArray(readingRegister)) {
    return readingRegister.map((register: any) => ({
      registerId: register.RegisterID,
      reading: register.Reading,
    }));
  }

  return [
    {
      registerId: readingRegister.RegisterID,
      reading: readingRegister.Reading,
    },
  ];
}

export function getXrdlgData(xrdlgResponse: any): XrdlgData[] {
  const deviceLogEntry = xrdlgResponse.atomL2response['0']?.xml_string?.Body?.XRDLG_OUT_02?.DeviceLogEntry;
  if (!deviceLogEntry) {
    return [];
  }

  if (Array.isArray(deviceLogEntry)) {
    return deviceLogEntry.map((device: any) => ({
      deviceId: device.DeviceID,
      lastCommunicatedTime: makeReadableTimeStamp(device.LastCommunicationTime),
      activeStatus: isLCTActive(device.LastCommunicationTime),
    }));
  }

  return [
    {
      deviceId: deviceLogEntry.DeviceID,
      lastCommunicatedTime: makeReadableTimeStamp(deviceLogEntry.LastCommunicationTime),
      activeStatus: isLCTActive(deviceLogEntry.LastCommunicationTime),
    },
  ];
}

export function mergeXrdlgIntoXrinv(xrinvDevices: XrinvData[], xrdlgDevices: XrdlgData[]): XrinvData[] {
  const logEntriesByDeviceId = new Map(xrdlgDevices.map((xrdlgDevice) => [xrdlgDevice.deviceId, xrdlgDevice]));

  return xrinvDevices.map((invDevice) => {
    const logEntry = logEntriesByDeviceId.get(invDevice.deviceId);
    const isSmartMeter = invDevice.deviceType === 'ESME' || invDevice.deviceType === 'GSME';

    return {
      ...invDevice,
      lastCommunicatedTime: logEntry ? logEntry.lastCommunicatedTime : invDevice.lastCommunicatedTime,
      activeStatus: isSmartMeter && logEntry ? logEntry.activeStatus : null,
    };
  });
}
